/**
 * Collector Management — llama DIRECTO a las APIs que ya usaba webadmin v1:
 *
 *   adminapi        https://api.cucuru.com/admin/v1   (envelope {data,message,statusCode,hasErrors}, camelCase)
 *   CucuruCoreDBApi https://api.cucuru.com/app/v1     (DTO pelado, snake_case)
 *
 * Ambas validan `Authorization: Bearer <jwt>` con las claves HS384 de CoreDB, o
 * sea el token del 1er factor (`webadmin2.corejwt`), igual que bind.ts. El token
 * post-MFA (`webadmin2.jwt`) lo firma webadmin2 y estas APIs no lo conocen.
 *
 * Contratos verificados en los repos adminapi / CucuruCoreDBApi (2026-08-29) —
 * ver docs/collector-management-v2-design.md §4 para las diferencias con v1.
 */

import { parseBankAccount } from "../lib/userAccounts";
import { mapUploadTask, type UploadTask } from "../lib/uploadTasks";

const ADMIN_API =
  (import.meta.env.VITE_ADMIN_API as string | undefined) ?? "https://api.cucuru.com/app";
/** adminapi cuelga del mismo host que CoreDB pero bajo /admin. */
const ADMINAPI =
  (import.meta.env.VITE_ADMINAPI as string | undefined) ??
  ADMIN_API.replace(/\/app\/?$/, "/admin");

export const PAYMENT_PROCESSORS = ["AR#BIND", "AR#CMFP", "MX#STP"] as const;

export const USER_ROLES = [
  { key: "main",     description: "Admin plus user management" },
  { key: "admin",    description: "Full access, cannot create users" },
  { key: "standard", description: "Views payments and settlements" },
  { key: "pos",      description: "Views payments only" },
] as const;

// ----- tipos normalizados ----------------------------------------------------

export interface Collector {
  id:                string;
  paymentsProcessor: string;      // "AR#BIND" | "AR#CMFP" | "MX#STP" | "NA"
  closeId:           string | null;
  active:            boolean;
  settlementEnabled: boolean;
  /** No viene en el listado (vive en RDS `vendor`); queda para el detalle si algún día se expone. */
  doingBusinessAs:   string;
}

/** key → value_str ("" cuando fue "nullified"). */
export type CollectorConfig = Record<string, string>;

export interface ConfigKeyMeta {
  key:         string;
  description: string;
  country:     string;     // "AR", "MX", "AR,MX", ""…
  readOnly:    boolean;
  type:        string;
  validation:  string;     // regex que aplica el backend al upsert
  widget:      string;
  defaultValue: string;
  /** Cuando widget === "combo": opciones ya resueltas (value crudo + label linda). */
  options?: { value: string; label: string }[];
}
export type ConfigCatalog = Record<string, ConfigKeyMeta>;

/** widget "combo" trae el value_str como `[{"raw":"Label"}, ...]` — un objeto de 1 key por opción. */
function parseComboOptions(raw: string): { value: string; label: string }[] {
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.flatMap((o) => (o && typeof o === "object") ? Object.entries(o).map(([value, label]) => ({ value, label: String(label) })) : []);
  } catch { return []; }
}

export interface Account {
  accountNumber: string;
  customerId:    string;
  alias:         string;
  email:         string | null;
  onReceived:    "accept" | "reject";
  deleted:       boolean;
  /** CP-484: "Descripción" del portal vendor (`payment_accounts.data`). "" si no tiene
   *  o si el listado vino del endpoint viejo (que no la devuelve). */
  description:   string;
}

export interface CollectorUser {
  email:          string;   // sin sufijo de canal
  channel:        string;   // "vendorweb"
  role:           string;
  lastLogin:      string | null;
  appVersion:     string | null;
  failedAttempts: number;
  createdAt:      string | null;
  accounts:       string[] | null;   // Id Cliente asignados (users.bank_account); null = todas
}

// UploadTask y su mapeo viven en lib/uploadTasks.ts (CP-457) para poder testearlos.
export type { UploadTask } from "../lib/uploadTasks";

/**
 * Línea del ledger de settlements de un collector (`GET /SettlementsSummary`).
 * `pk`/`sk` identifican la fila para el tag; `collectionPk`/`collectionSk` son
 * las claves que exige `changeTransferStatus` — son pares distintos en la API.
 */
export interface Movement {
  pk:               string;
  sk:               string;
  collectionPk:     string;
  collectionSk:     string;
  businessDate:     string;
  dateTime:         string;
  tag:              string;
  operationType:    string;
  description:      string;
  status:           string;
  currency:         string;
  amount:           number;
  availableBalance: number;
}

export interface CollectorMovementsSummary {
  currency:        string;
  availableAmount: number;
  movements:       Movement[];
}

/** Tipado de inputs por clave (complementa `validation` del catálogo). */
export type ConfigKeyType =
  | { kind: "enum"; options: string[] }
  | { kind: "bool" } | { kind: "rate" } | { kind: "number" } | { kind: "long" };
export const CONFIG_KEY_TYPES: Record<string, ConfigKeyType> = {
  close_id:        { kind: "enum", options: ["AR_2PM", "MX_00"] },
  // CP-375: en el catálogo real (CONFIGKEYS) esta key es widget "textfield", no "combo" — no hay
  // value_str con opciones para parsear dinámicamente. Reutiliza los mismos valores que acepta
  // payments_processor (misma validación `^[^#]+#[^#]+$`).
  new_accounts:    { kind: "enum", options: [...PAYMENT_PROCESSORS] },
  create_accounts: { kind: "bool" },
  no_settlement:   { kind: "bool" },
  charge_rate:     { kind: "rate" },
  split_rate:      { kind: "rate" },
  acquirer:        { kind: "number" },
  economy_capacity_max_transaction: { kind: "number" },
  contract_html:   { kind: "long" },
};

// ----- http ------------------------------------------------------------------

function authHeader(): Record<string, string> {
  const jwt = localStorage.getItem("webadmin2.corejwt");
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

async function req(url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, { ...init, headers: { ...authHeader(), ...(init.headers ?? {}) } });
  if (res.status === 401) {
    // Sesión de CoreDB vencida: mismo tratamiento que bind.ts.
    localStorage.removeItem("webadmin2.jwt");
    localStorage.removeItem("webadmin2.corejwt");
    localStorage.removeItem("webadmin2.email");
    if (typeof window !== "undefined") window.location.replace("/login");
    throw new Error("Session expired — please sign in again.");
  }
  return res;
}

async function errorText(res: Response): Promise<string> {
  const txt = await res.text().catch(() => "");
  try {
    const j = JSON.parse(txt);
    // CMF alias errors: { errors: { respuesta: { numero, mensaje } } }
    const r = j?.errors?.respuesta;
    if (r?.mensaje) return `${r.mensaje} (${r.numero})`;
    if (typeof j?.data === "string") return j.data;
    if (typeof j?.message === "string") return j.message;
    // CP-435: ValidationProblemDetails de .NET: {title, errors: {campo: [motivos]}}
    if (typeof j?.title === "string") {
      const det = j?.errors && typeof j.errors === "object"
        ? Object.entries(j.errors).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(" ") : String(v)}`).join("; ")
        : "";
      return det ? `${j.title} ${det}` : j.title;
    }
  } catch { /* texto plano */ }
  return txt || `HTTP ${res.status}`;
}

/** adminapi: desenvuelve {data,message,statusCode,hasErrors}. */
async function adminApi<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await req(`${ADMINAPI}/v1${path}`, {
    ...init, headers: { Accept: "application/json", ...(init.body ? { "Content-Type": "application/json" } : {}), ...(init.headers ?? {}) },
  });
  const body = await res.json().catch(() => null);
  if (!res.ok || body?.hasErrors) {
    const msg = typeof body?.data === "string" ? body.data : body?.message;
    throw new Error(msg || `adminapi ${path}: HTTP ${res.status}`);
  }
  return body?.data as T;
}

/** CoreDB: DTO pelado; `parse` = "json" | "text" | "none". */
async function coreApi(path: string, init: RequestInit = {}, parse: "json" | "text" | "none" = "json"): Promise<any> {
  const isForm = init.body instanceof FormData;
  const res = await req(`${ADMIN_API}/v1${path}`, {
    ...init, headers: { Accept: "application/json", ...(init.body && !isForm ? { "Content-Type": "application/json" } : {}), ...(init.headers ?? {}) },
  });
  if (!res.ok) throw new Error(await errorText(res));
  if (parse === "none") return undefined;
  const txt = await res.text();
  if (parse === "text") return txt;
  if (!txt) return null;
  try { return JSON.parse(txt); } catch { return txt; }
}

// ----- collectors -------------------------------------------------------------

interface RawCollector { id: string; paymentProcessor?: string; closeId?: string }

/**
 * `GET /collectors` solo trae id/procesador/close. `active` y `settlement` se
 * derivan pidiendo también los subconjuntos filtrados (el backend compara los
 * strings tal cual, con defaults "true" / "0"). 3 llamadas en paralelo, cacheadas
 * del lado del server.
 */
export async function listCollectors(_opts: { all?: boolean } = {}): Promise<Collector[]> {
  const norm = (v?: string | null) => (!v || v === "NA" ? null : v);
  const q = (p: string) => adminApi<RawCollector[]>(`/collectors?${p}`).then((r) => r ?? []);
  const [all, activeS0, activeS1] = await Promise.all([
    q("all=true"),
    q("all=false&active=true&settlement_method_account_enabled=0"),
    q("all=false&active=true&settlement_method_account_enabled=1"),
  ]);
  const activeIds = new Set([...activeS0, ...activeS1].map((c) => c.id));
  const settleIds = new Set(activeS1.map((c) => c.id));
  return all.map((c) => ({
    id:                c.id,
    paymentsProcessor: norm(c.paymentProcessor) ?? "NA",
    closeId:           norm(c.closeId),
    active:            activeIds.has(c.id),
    settlementEnabled: settleIds.has(c.id),
    doingBusinessAs:   "",
  }));
}

/**
 * CP-445: arma el Collector desde SU config (1 request), con el mismo criterio que
 * `GetAllCollectorsV2` de adminapi: defaults "NA" / "true" / "0" y activo si
 * active == "true" con settlement "0" o "1". Antes `getCollector` llamaba a
 * `listCollectors()` (3 requests que recorren los ~720 collectors en adminapi) y
 * en prd terminaba en "Failed to fetch". Sin config → el collector no existe.
 */
export function collectorFromConfig(id: string, cfg: CollectorConfig): Collector | null {
  if (!Object.keys(cfg).length) return null;
  const v = (k: string, def: string) => cfg[k] || def;   // "" = nullified → default
  const active = v("active", "true");
  const settlement = v("settlement_method_account_enabled", "0");
  const isActive = active === "true" && (settlement === "0" || settlement === "1");
  const closeId = v("close_id", "NA");
  return {
    id,
    paymentsProcessor: v("payments_processor", "NA"),
    closeId:           closeId === "NA" ? null : closeId,
    active:            isActive,
    settlementEnabled: isActive && settlement === "1",
    doingBusinessAs:   "",
  };
}

export async function getCollector(id: string): Promise<Collector | null> {
  return collectorFromConfig(id, await getCollectorConfig(id));
}

export async function createCollector(c: { id: string; paymentsProcessor: string; doingBusinessAs: string }): Promise<void> {
  await adminApi<string>("/collector", {
    method: "POST",
    body: JSON.stringify({
      collector_id: c.id, payments_processor: c.paymentsProcessor,
      doing_business_as: c.doingBusinessAs || c.id, config_data: {},
    }),
  });
}

/** Lista de collectors con onboarding pendiente (config `onboarding = pending`). */
export async function listOnboardingPending(): Promise<string[]> {
  return (await adminApi<string[]>("/collectors/onboarding")) ?? [];
}

// ----- config -----------------------------------------------------------------

export async function getConfigCatalog(): Promise<ConfigCatalog> {
  const rows = (await adminApi<any[]>("/collectors/configkeys")) ?? [];
  const out: ConfigCatalog = {};
  for (const r of rows) {
    if (!r?.id) continue;
    const widget = r.widget ?? "";
    out[r.id] = {
      key: r.id, description: r.description ?? "", country: r.country ?? "",
      readOnly: /^(true|1|yes)$/i.test(String(r.read_only ?? r.readOnly ?? "")),
      type: r.type ?? "", validation: r.validation ?? "", widget, defaultValue: r.value ?? "",
      options: widget === "combo" ? parseComboOptions(r.value ?? "") : undefined,
    };
  }
  return out;
}

export async function getCollectorConfig(id: string): Promise<CollectorConfig> {
  const data = await adminApi<{ collectorId: string; collectionConfig: { configKey: string; valueStr: string | null }[] }>(
    `/collectors/${encodeURIComponent(id)}/Config`);
  const out: CollectorConfig = {};
  for (const kv of data?.collectionConfig ?? []) out[kv.configKey] = kv.valueStr ?? "";
  return out;
}

/**
 * CP-488: adminapi rechaza modificar `payments_processor` ("payments_processor is readonly,
 * cannot be modified"). WA1 usa para eso un endpoint aparte de CoreDB,
 * `POST /Admin/ModifyPaymentsProcessor`, que pisa solo ese valor y exige admin de prd
 * (claim prod / iid -999). Fetch propio: un 401 de ESTE endpoint (admin que no es de prd)
 * no tiene que cerrar la sesión, como haría coreApi. 404 = el collector no tiene la clave:
 * se da de alta por adminapi como cualquier otra.
 */
async function modifyPaymentsProcessor(c: Collector, value: string): Promise<boolean> {
  const res = await fetch(`${ADMIN_API}/v1/Admin/ModifyPaymentsProcessor`, {
    method: "POST",
    headers: { ...authHeader(), Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ collector_id: c.id, new_payments_processor: value }),
  });
  if (res.status === 404) return false;
  if (res.status === 401 || res.status === 403)
    throw new Error("Only production admins can change payments_processor.");
  if (!res.ok) throw new Error(await errorText(res));
  return true;
}

/** Alta o modificación de una clave (POST /collector con config_data {k: v}). */
export async function setConfigValue(c: Collector, key: string, value: string): Promise<void> {
  if (key === "payments_processor" && await modifyPaymentsProcessor(c, value)) return;
  await adminApi<string>("/collector", {
    method: "POST",
    body: JSON.stringify({ collector_id: c.id, payments_processor: c.paymentsProcessor, config_data: { [key]: value } }),
  });
}

/** PUT /collector/Config: el verbo va como VALOR de config_data y debe ir en minúscula. */
async function configVerb(c: Collector, key: string, verb: "delete" | "nullify"): Promise<void> {
  await adminApi<string>("/collector/Config", {
    method: "PUT",
    body: JSON.stringify({ collector_id: c.id, payments_processor: c.paymentsProcessor, config_data: { [key]: verb } }),
  });
}
export const clearConfigValue = (c: Collector, key: string) => configVerb(c, key, "nullify");
export const removeConfigKey  = (c: Collector, key: string) => configVerb(c, key, "delete");

// ----- accounts ---------------------------------------------------------------

function _accountFromApi(a: any): Account {
  return {
    accountNumber: String(a?.account_number ?? ""),
    customerId:    String(a?.customer_id ?? ""),
    alias:         String(a?.alias ?? ""),
    email:         a?.email ?? null,
    onReceived:    a?.on_received === "reject" ? "reject" : "accept",
    deleted:       String(a?.deleted ?? "no").toLowerCase() === "yes",
    description:   String(a?.data ?? "").trim(),
  };
}

export async function listAccounts(collectorId: string, nextPage?: string | null): Promise<{ items: Account[]; nextPage: string | null }> {
  const qs = new URLSearchParams({ collector_id: collectorId });
  if (nextPage) qs.set("next_page", nextPage);
  // CP-484: primero el listado del portal (`webp/accounts`), que trae también la
  // descripción (`data`); el token de admin pasa su chequeo (isAdmin). Se llama con un
  // fetch propio para que un 401/403 de ESE endpoint no cierre la sesión (coreApi lo
  // haría): si falla en la primera página, se usa el listado de siempre sin descripción
  // y se recuerda para el resto de la sesión. Los cursores de los dos endpoints no se
  // mezclan: una página siguiente siempre usa el mismo endpoint que la primera.
  if (!webpAccountsUnavailable) {
    const qp = new URLSearchParams({ product_id: collectorId });
    if (nextPage) qp.set("next_page", nextPage);
    try {
      const res = await fetch(`${ADMIN_API}/v1/Collection/webp/accounts?${qp}`, { headers: { ...authHeader(), Accept: "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const items: Account[] = (data?.accounts ?? []).map(_accountFromApi);
      const np = data?.next_page;
      return { items, nextPage: np && np !== "EOF" ? String(np) : null };
    } catch (e) {
      if (nextPage) throw e;   // a mitad de la paginación no se puede cambiar de endpoint
      webpAccountsUnavailable = true;
      console.warn("webp/accounts no disponible, se usa web/accounts (sin descripción):", e);
    }
  }
  const data = await coreApi(`/Collection/web/accounts?${qs}`);
  const items: Account[] = (data?.accounts ?? []).map(_accountFromApi);
  const np = data?.next_page;
  return { items, nextPage: np && np !== "EOF" ? String(np) : null };
}

let webpAccountsUnavailable = false;

/**
 * CP-458: cuenta puntual por account_number — usado para "resolver" un hit del
 * buscador de alias/CVU (services/monitoreoVivo.ts) que no está en la página ya
 * cargada de `listAccounts`, sin traer todo el collector. Mismo endpoint que ya
 * usa el apikey externo (`GetAccountWEB` en CoreDB), nada nuevo del lado del backend.
 */
export async function getAccount(collectorId: string, accountNumber: string): Promise<Account | null> {
  try {
    const data = await coreApi(`/Collection/web/accounts/account/${encodeURIComponent(accountNumber)}?${new URLSearchParams({ collector_id: collectorId })}`);
    return data?.account_number ? _accountFromApi(data) : null;
  } catch {
    return null;
  }
}

/** Agota la paginación de `listAccounts` — usado por el export (CP-425) y el preview de alta masiva (CP-444). */
export async function listAllAccounts(collectorId: string): Promise<Account[]> {
  const all: Account[] = [];
  let cursor: string | null = null;
  do {
    const r = await listAccounts(collectorId, cursor);
    all.push(...r.items);
    cursor = r.nextPage;
  } while (cursor);
  return all;
}

export async function addAccount(collectorId: string, customerId: string): Promise<string> {
  const data = await coreApi("/Collection/web/accounts/account", {
    method: "PUT", body: JSON.stringify({ collector_id: collectorId, customer_id: customerId }),
  });
  return String(data?.account_number ?? "");
}

export async function editAccountCustomerAndEmail(collectorId: string, accountNumber: string, customerId: string, email: string): Promise<void> {
  await coreApi("/Collection/web/accounts/account", {
    method: "POST", body: JSON.stringify({ collector_id: collectorId, account_number: accountNumber, customer_id: customerId, email }),
  }, "none");
}

export async function editAccountAlias(collectorId: string, accountNumber: string, alias: string): Promise<void> {
  const r = await coreApi("/Collection/web/accounts/account/alias", {
    method: "POST", body: JSON.stringify({ collector_id: collectorId, account_number: accountNumber, alias }),
  }, "text");
  if (/alias ya utilizado|cannot be empty/i.test(String(r))) throw new Error(String(r).replace(/^"|"$/g, ""));
}

/**
 * Habilitar / deshabilitar la CVU en el banco (mismos endpoints que v1).
 * OJO: `enablecvuaccount` solo busca `AR#BIND#<cvu>` en el backend — para CMFP
 * la re-habilitación falla con 400 hasta que se corrija en CoreDB.
 */
export async function setAccountEnabled(a: Account, enabled: boolean): Promise<void> {
  try {
    await coreApi(`/Admin/bind/web/${enabled ? "enable" : "disable"}cvuaccount`, {
      method: "POST", body: JSON.stringify({ cvu: a.accountNumber }),
    }, "none");
  } catch (e: any) {
    // El backend responde 400 sin detalle; la causa real queda en CloudWatch
    // (/aws/ecs/apps_cucuru_prd, "bind/web/enablecvuaccount: ..."). Traducimos
    // a las causas conocidas del código de CoreDB.
    if (/^(HTTP 400|Bad Request)$/i.test(String(e?.message ?? ""))) {
      throw new Error(enabled
        ? "The bank rejected the request (HTTP 400). Known causes: the account is already enabled; the collector's payments_sub_processor is not bindpsp/cvucollect_cucuru (re-enable is only implemented for those); the account has no tax id; or it is a CMFP account (re-enable only looks up AR#BIND). Details are in the API logs."
        : "The bank rejected the request (HTTP 400). Known causes: the account is already disabled, has no tax id, or the provider call failed. Details are in the API logs.");
    }
    throw e;
  }
}

// ----- uploads ----------------------------------------------------------------

export async function listUploadTasks(collectorId: string): Promise<UploadTask[]> {
  const data = await adminApi<{ collectorId: string; taskMonitors: any[] }>(`/collectors/${encodeURIComponent(collectorId)}/TaskMonitor`);
  return (data?.taskMonitors ?? []).map((t) => mapUploadTask(t, collectorId));
}

/**
 * CP-484: escribe la "Descripción" de una cuenta (la que ve el collector en el portal;
 * CoreDB la guarda en `payment_accounts.data`). Mismo endpoint que el portal vendor.
 * Con solo `account_number` + `description`, CoreDB no toca alias/email/customer_id
 * (van null), pero fuerza `on_received="accept"`: NO llamarlo para cuentas
 * deshabilitadas o rechazando cobros — las reactivaría.
 * El token de admin (isAdmin + rol EAC) pasa los chequeos del endpoint.
 */
export async function setAccountDescription(collectorId: string, accountNumber: string, description: string): Promise<void> {
  const r = await coreApi("/Collection/accounts/web_account", {
    method: "POST",
    body: JSON.stringify({ collector_id: collectorId, account_number: accountNumber, description }),
  });
  const proc = String(r?.processResult ?? "");
  const data = String(r?.updateDataResult ?? "");
  if (proc !== "ok" || data !== "ok") {
    throw new Error(proc === "no_available_accounts"
      ? "The collector has no account slots left (user_accounts_available)."
      : `CoreDB did not save it (processResult=${proc || "—"}, updateDataResult=${data || "—"}).`);
  }
}

/** Carga masiva de cuentas: multipart con FormFile + userType=collector, userId=<id>, action=createcvu. */
export async function uploadAccountsFile(collectorId: string, file: File): Promise<void> {
  const fd = new FormData();
  fd.append("FormFile", file, file.name);
  fd.append("userType", "collector");
  fd.append("userId", collectorId);
  fd.append("action", "createcvu");
  await coreApi("/Admin/async_tasks/upload", { method: "POST", body: fd }, "none");
}

/** El download devuelve `{"url": "..."}` como text/plain; el pk lleva el prefijo `collector#`. */
export async function downloadUploadFile(t: UploadTask): Promise<void> {
  const pk = t.pk.startsWith("collector#") ? t.pk : `collector#${t.pk}`;
  const txt: string = await coreApi(`/Admin/async_tasks/download?${new URLSearchParams({ pk, sk: t.sk })}`, {}, "text");
  let url = "";
  try { url = JSON.parse(txt)?.url ?? ""; } catch { url = ""; }
  if (!url) throw new Error("The processed file is not available yet.");
  window.open(url, "_blank", "noopener");
}

// ----- users ------------------------------------------------------------------

export async function listUsers(collectorId: string): Promise<CollectorUser[]> {
  const rows: any[] = (await coreApi(`/Admin/collector/login_user?${new URLSearchParams({ collector_id: collectorId })}`)) ?? [];
  return rows.map((u) => {
    const [email, channel = ""] = String(u.email ?? "").split("#");
    const [ll, ver = ""] = String(u.last_login ?? "").split("|");
    // "20260813T16:13:46Z" → ISO
    const iso = /^\d{8}T\d{2}:\d{2}:\d{2}Z$/.test(ll) ? `${ll.slice(0, 4)}-${ll.slice(4, 6)}-${ll.slice(6, 8)}${ll.slice(8)}` : (ll || null);
    return {
      email, channel, role: String(u.role ?? ""), lastLogin: iso, appVersion: ver || null,
      failedAttempts: Number(u.failed_login_attempts ?? 0) || 0, createdAt: u.create_datetime ?? null,
      accounts: parseBankAccount(u.bank_account),
    };
  });
}

export async function addUser(collectorId: string, email: string, role: string): Promise<void> {
  await coreApi("/Admin/collector/login_user", {
    method: "POST", body: JSON.stringify({ collector_id: collectorId, email, role }),
  }, "none");
}

// ----- movements ("Collector Operation" en v1) --------------------------------

/**
 * Ledger de settlements del collector, sólo los últimos 10 días (límite del
 * propio backend, no de esta función). `country` va como el ISO2 del
 * `paymentsProcessor` (`AR#BIND` → `AR`), igual que en `ConfigTab.tsx`.
 */
export async function getCollectorMovements(collector: Collector): Promise<CollectorMovementsSummary> {
  const qs = new URLSearchParams({ product_id: collector.id, country: collector.paymentsProcessor.slice(0, 2) });
  const data = await coreApi(`/Admin/SettlementsSummary?${qs}`);
  const details: any[] = data?.details ?? [];
  return {
    currency:        String(data?.currency ?? ""),
    availableAmount: Number(data?.settlements_available_amount ?? 0) || 0,
    movements: details.map((m) => ({
      pk:               String(m.pk ?? ""),
      sk:               String(m.sk ?? ""),
      collectionPk:     String(m.collection_pk ?? ""),
      collectionSk:     String(m.collection_sk ?? ""),
      businessDate:     String(m.business_date ?? ""),
      dateTime:         String(m.date_time ?? ""),
      tag:              String(m.tag ?? ""),
      operationType:    String(m.operation_type ?? ""),
      description:      String(m.description ?? ""),
      status:           String(m.status ?? ""),
      currency:         String(m.currency ?? ""),
      amount:           Number(m.amount ?? 0) || 0,
      availableBalance: Number(m.available_balance ?? 0) || 0,
    })),
  };
}

/** Alta manual de un movimiento sobre el ledger del collector (`account` = collectorId, no una cuenta puntual). */
export async function addMovement(
  collector: Collector,
  params: { currency: string; amount: number; operationType: string; description: string },
): Promise<void> {
  await coreApi("/Admin/collector/account/addMovement", {
    method: "POST",
    body: JSON.stringify({
      account: collector.id, currency: params.currency, operation_external_id: "",
      amount: params.amount, operation_type: params.operationType, description: params.description,
    }),
  }, "none");
}

export async function addMovementTag(collectorId: string, m: Pick<Movement, "pk" | "sk">, tag: string): Promise<void> {
  await coreApi("/Admin/SettlementsSummaryTag", {
    method: "POST",
    body: JSON.stringify({ pk: m.pk, sk: m.sk, product_id: collectorId, new_Tag: tag }),
  }, "none");
}

/** Sólo tiene efecto si el movimiento está en `completed` / `failed` / `sent` (lo valida el backend). */
export async function changeTransferStatus(
  collectorId: string, m: Pick<Movement, "collectionPk" | "collectionSk">, newStatus: string,
): Promise<void> {
  await coreApi("/Admin/SetTransferStatus", {
    method: "POST",
    body: JSON.stringify({ collection_pk: m.collectionPk, collection_sk: m.collectionSk, product_id: collectorId, new_status: newStatus }),
  }, "none");
}

// ----- misc -------------------------------------------------------------------

/** Reporte de estado de cuentas (yolopago_update "accountsummary"). Requiere claim `prod`. */
export async function sendCollectorsReport(_collectorId?: string): Promise<void> {
  await coreApi("/Admin/execCollectorsReport", { method: "POST" }, "none");
}

export async function downloadContractPdf(collectorId: string): Promise<void> {
  const data = await coreApi(`/Onboarding/contract_pdf?${new URLSearchParams({ collector_id: collectorId })}`);
  const url = data?.url ?? data?.Url;
  if (!url) throw new Error("No contract available for this collector.");
  window.open(url, "_blank", "noopener");
}
